import React, { FC, ChangeEvent } from 'react';
import {
  Button,
  FormControlLabel,
  makeStyles,
  Switch,
  TableRow,
  TableCell,
} from '@material-ui/core';
import Edit from '@material-ui/icons/Edit';
import { useTranslation } from 'react-i18next';
import { generateLink } from 'Links';

import { useNotify } from 'hooks';

import {
  UserSegment,
  GenericStatus,
  useModifyContentEmailMutation,
  ContentEmailSpecFragment,
} from 'api';
import DateCell from './DateCell';
import MessageCell from './MessageCell';
import DeliveryInterval from './DeliveryInterval';

interface IProps {
  item: ContentEmailSpecFragment;
  index: number;
  segments: UserSegment[];
  numActiveUsers: number;
}

const useStyles = makeStyles(theme => ({
  row: {
    verticalAlign: 'top',
    marginTop: theme.spacing(1),
  },
  cell: {
    paddingTop: theme.spacing(1),
  },
  title: {
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(2),
    display: 'inline-block',
  },
}));

const EmailRow: FC<IProps> = props => {
  const { item, index, segments, numActiveUsers } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const notify = useNotify();
  const [modifyEmailMutation] = useModifyContentEmailMutation({
    onError: notify.mutationError,
    onCompleted: () => {
      notify.info({ message: t('components:savedSuccessfully') });
    },
  });

  function renderEnableSwitch(id: ContentEmailSpecFragment['id'], checked: boolean) {
    function handleUpdateStatus(event: ChangeEvent<HTMLInputElement>) {
      const value = event.target.checked ? GenericStatus.Active : GenericStatus.Disabled;
      modifyEmailMutation({
        variables: { data: { id, config_status: value } },
        optimisticResponse: {
          modifyContentEmailSpec: {
            ...item,
            config_status: value,
          },
        },
      });
    }

    return (
      <FormControlLabel
        label={t('common:enable')}
        labelPlacement="start"
        control={<Switch checked={checked} onChange={handleUpdateStatus} color="primary" />}
      />
    );
  }

  return (
    <TableRow key={index} className={classes.row}>
      <DateCell
        deliveryInterval={<DeliveryInterval interval={item.delivery_interval} />}
        sendAt={item.send_at}
      />
      <MessageCell
        segments={segments}
        numActiveUsers={numActiveUsers}
        id={item.id}
        title={item.title}
        segmentIds={item.segment_ids}
        subject={item.subject}
        intro={item.intro}
        contentSpecs={item.content_specs.length}
        postTypes={item.content_specs}
      />
      <TableCell style={{ width: '10%' }}>
        <Button
          component={generateLink(`/admin/emails/${item.id}`)}
          endIcon={<Edit fontSize="small" />}>
          {t('common:edit')}
        </Button>
      </TableCell>
      <TableCell style={{ width: '12%' }}>
        {renderEnableSwitch(item.id, item.config_status === GenericStatus.Active)}
      </TableCell>
    </TableRow>
  );
};

export default EmailRow;
